import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getComicById, comics } from '@/mocks/comics';

const READING_PAGES = [
  'https://readdy.ai/api/search-image?query=Black%20and%20white%20manga%20page%20style%20illustration%20with%20dynamic%20action%20scene%2C%20Japanese%20comic%20panel%20layout%2C%20characters%20in%20dramatic%20combat%20pose%2C%20speed%20lines%2C%20detailed%20linework%2C%20clean%20digital%20scan%20quality&width=800&height=1200&seq=reading-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Black%20and%20white%20manga%20page%20with%20dialog%20and%20character%20closeup%2C%20emotional%20expressions%2C%20Japanese%20comic%20style%20panels%2C%20detailed%20background%20art%2C%20clean%20line%20art%2C%20digital%20manga%20scan&width=800&height=1200&seq=reading-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Black%20and%20white%20manga%20action%20sequence%20with%20impact%20effects%2C%20silhouette%20figures%2C%20dramatic%20shading%2C%20Japanese%20comic%20art%2C%20dynamic%20panel%20composition%2C%20clean%20digital%20illustration&width=800&height=1200&seq=reading-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Manga%20page%20featuring%20detailed%20cityscape%20background%2C%20characters%20walking%20scene%2C%20Japanese%20comic%20style%2C%20clean%20linework%2C%20atmospheric%20shading%2C%20digital%20scan%20quality&width=800&height=1200&seq=reading-page-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Black%20and%20white%20manga%20climax%20scene%20with%20large%20panel%2C%20emotional%20moment%2C%20detailed%20character%20art%2C%20speed%20lines%20and%20effects%2C%20Japanese%20comic%20style%2C%20clean%20digital%20quality&width=800&height=1200&seq=reading-page-05&orientation=portrait',
];

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <>
        <SEOHead
          title="漫画未找到"
          description="抱歉，您要查找的漫画不存在或已下架。"
          keywords="亲亲漫画网,漫画未找到"
          canonicalUrl={`/comic/${id}`}
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center py-20">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
              <p className="text-foreground-500 mb-6">抱歉，您要查找的漫画不存在</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回漫画列表
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const seoStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 亲亲漫画网${comic.status === '连载中' ? '连载' : '完结'}漫画`,
      description: `${comic.title}：${comic.description}`,
      author: { '@type': 'Person', name: comic.author },
      datePublished: comic.chapters[0]?.updatedAt || comic.updatedAt,
      dateModified: comic.updatedAt,
      image: comic.cover,
      publisher: {
        '@type': 'Organization',
        name: '亲亲漫画网',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qinqinmanga.com/' },
        { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `https://qinqinmanga.com/category/${comic.category}` },
        { '@type': 'ListItem', position: 3, name: comic.title },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`${comic.title} - 亲亲漫画网免费下拉式在线看`}
        description={`${comic.title}：${comic.description.slice(0, 120)}... 亲亲漫画网提供免费下拉式阅读，下拉式全集在线看，流畅滑动沉浸式漫画体验。`}
        keywords={`${comic.title},${comic.category}下拉式,${comic.tags.join(',')},下拉式漫画,免费下拉式看漫画,亲亲漫画网,在线看漫画`}
        canonicalUrl={`/comic/${comic.id}`}
        ogImage={comic.cover}
        ogType="article"
        structuredData={seoStructuredData}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 w-full px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${comic.category}漫画`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            {/* Comic Info Header */}
            <article className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
              <div className="w-full md:w-56 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 亲亲漫画网下拉式在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                      comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-600'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="text-sm text-foreground-500 flex items-center gap-1">
                      <i className="ri-user-line text-sm"></i>
                      {comic.author}
                    </span>
                    <span className="text-sm text-foreground-500 flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                      {comic.rating}
                    </span>
                    <span className="text-sm text-foreground-500 flex items-center gap-1">
                      <i className="ri-eye-line text-sm"></i>
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 阅读
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {comic.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/${tag}`}
                        className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-6">
                  <a
                    href="#reading-section"
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-book-open-line"></i>
                    开始阅读
                  </a>
                  <Link
                    to={`/category/${comic.category}`}
                    className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-600 px-5 py-2.5 rounded-full font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    返回列表
                  </Link>
                </div>
              </div>
            </article>

            {/* Chapter List */}
            <section className="mb-10">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-list-check text-primary-500"></i>
                章节列表
                <span className="text-sm font-normal text-foreground-500">
                  （共 {comic.chapters.length} 话）
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <a
                    key={chapter.id}
                    href="#reading-section"
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group border border-transparent hover:border-primary-200"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 font-medium">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                  </a>
                ))}
              </div>
            </section>

            {/* Reading Experience - Scroll Down */}
            <section id="reading-section" className="mb-10">
              <div className="bg-background-100 rounded-xl p-4 md:p-6 mb-4 text-center">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-1 flex items-center justify-center gap-2">
                  <i className="ri-scroll-to-bottom-line text-primary-500"></i>
                  下拉式阅读体验
                </h2>
                <p className="text-sm text-foreground-500 flex items-center justify-center gap-1 animate-pulse-soft">
                  <i className="ri-arrow-down-line"></i>
                  向下滑动即可连续阅读，享受流畅的漫画体验
                  <i className="ri-arrow-down-line"></i>
                </p>
              </div>

              <div className="flex flex-col items-center gap-0 max-w-2xl mx-auto">
                {READING_PAGES.map((page, i) => (
                  <div key={i} className="w-full">
                    <img
                      src={page}
                      alt={`${comic.title} 第${i + 1}页 - 下拉式漫画阅读`}
                      title={`${comic.title}下拉式漫画阅读 - 第${i + 1}页`}
                      className="w-full h-auto"
                    />
                  </div>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-4 text-center mt-4">
                <p className="text-sm text-foreground-500">
                  以上为下拉式阅读体验演示。实际阅读时，漫画将以连续滚动的形式呈现，无需翻页。
                </p>
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section className="pb-14">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-heart-line text-primary-500"></i>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} variant="compact" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}